import type { Prisma } from '@prisma/client';

/**
 * The canonical record guards. Do not hand-roll local copies of these — a
 * GritQL rule (gritQL/record-guards-plugin.grit) rejects new declarations
 * named isRecord / asRecord / isPlainObject / isJsonObject outside this file.
 */

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function asRecord(value: unknown): Record<string, unknown> | null {
  return isRecord(value) ? value : null;
}

/**
 * `isRecord` for values read from Prisma JSON columns — narrows the values to
 * `Prisma.JsonValue`. That narrowing is only sound when the input actually
 * came from a JSON column; for arbitrary `unknown` use `isRecord`.
 */
export function isJsonObject(
  value: unknown,
): value is Record<string, Prisma.JsonValue> {
  return isRecord(value);
}

/**
 * `isRecord` restricted to plain objects (lodash semantics): prototype is
 * `Object.prototype` or null. Rejects class instances (Date, Map, Error, …)
 * that generic spread/clone/redact code must not treat as bags of fields.
 */
export function isPlainObject(
  value: unknown,
): value is Record<string, unknown> {
  if (!isRecord(value)) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}
